import { lstatSync, mkdirSync, readlinkSync, rmSync, symlinkSync, type Stats } from 'node:fs';
import { dirname } from 'node:path';

import { setEngineLogger } from '@/core/logger-registry.js';
import { getRuntimeRoot } from '@/core/runtime-paths.js';
import { ensureSchemaMarkerSync } from '@/core/schema-version.js';
import type { EngineLogger } from '@/core/types/logger.js';
import { VERSION } from '@/index.js';
import {
  resolveFrameworkInstallPath,
  writeFrameworkMetadata,
} from '@/onboarding/manifest-writer.js';
import { writeStageAgents } from '@/stage-isolation/agent-writer.js';

export interface InstallResult {
  framework_home: string;
  project_root: string;
  version: string;
}

/** Options for {@link bootstrapFramework}. */
export interface BootstrapOptions {
  /**
   * A consumer logger to install at init (PQD-105). When supplied, every
   * structured log the engine emits is routed to it. Equivalent to calling
   * {@link setEngineLogger} before bootstrapping; provided here so the consumer
   * has a single clean init surface.
   */
  logger?: EngineLogger;
}

/**
 * The HOME-ONLY half of the install: create the `~/.paqad-ai/current` framework symlink and
 * render the six stage-isolation agents under the user home. It writes NOTHING into the project,
 * so a caller that must not touch the tracked tree (issue #576, Finding 2 — `paqad-ai join`) can
 * set up a fresh machine's global install without producing a diff. {@link bootstrapFramework}
 * layers the project-side metadata writes on top for `onboard`/`install`.
 */
export function bootstrapFrameworkHome(): { framework_home: string } {
  const frameworkHome = resolveFrameworkInstallPath();
  const runtimeRoot = getRuntimeRoot();

  ensureFrameworkSymlink(runtimeRoot, frameworkHome);

  // Issue #567 — render the six stage agents at user scope (~/.claude/agents, ~/.codex/agents).
  // Stage isolation is core-engine behavior (no config knob), so this always runs; it never
  // writes into the project. Best-effort: a render/write failure must not fail the install (the
  // agents are inert until the orchestrator dispatches them, and the next update retries).
  try {
    writeStageAgents();
  } catch {
    /* best-effort: stage-agent generation never blocks install/update */
  }

  return { framework_home: frameworkHome };
}

export function bootstrapFramework(projectRoot: string, options?: BootstrapOptions): InstallResult {
  if (options?.logger !== undefined) {
    setEngineLogger(options.logger);
  }

  const { framework_home: frameworkHome } = bootstrapFrameworkHome();

  writeFrameworkMetadata(projectRoot, VERSION);

  // PQD-95 — ensure the cross-artifact schema marker exists so every freshly
  // bootstrapped project carries the `.paqad/` layout version. Idempotent: an
  // existing marker is left untouched (migration is checkAndMigrateSchema's job).
  ensureSchemaMarkerSync(projectRoot, VERSION);

  return {
    framework_home: frameworkHome,
    project_root: projectRoot,
    version: VERSION,
  };
}

function ensureFrameworkSymlink(runtimeRoot: string, frameworkHome: string): void {
  mkdirSync(dirname(frameworkHome), { recursive: true });

  if (isExpectedRuntimeSymlink(runtimeRoot, frameworkHome)) {
    return;
  }

  removeConflictingFrameworkPath(frameworkHome);

  try {
    symlinkSync(runtimeRoot, frameworkHome);
  } catch (error) {
    if (
      error instanceof Error &&
      'code' in error &&
      error.code === 'EEXIST' &&
      isExpectedRuntimeSymlink(runtimeRoot, frameworkHome)
    ) {
      return;
    }
    throw error;
  }
}

function removeConflictingFrameworkPath(frameworkHome: string): void {
  // Use lstat, not existsSync: existsSync follows symlinks, so a dangling link
  // (e.g. the npx cache dir it pointed at was garbage-collected between runs)
  // reads as "absent" — skipping cleanup and crashing symlinkSync with EEXIST.
  const stat = lstatSafe(frameworkHome);
  if (stat === undefined) {
    return;
  }

  if (stat.isDirectory() && !stat.isSymbolicLink()) {
    throw new Error(
      `Refusing to replace existing framework home directory at ${frameworkHome}. Remove it manually or point PAQAD_FRAMEWORK_HOME at an empty path.`,
    );
  }

  rmSync(frameworkHome, { force: true, recursive: false });
}

function isExpectedRuntimeSymlink(runtimeRoot: string, frameworkHome: string): boolean {
  const stat = lstatSafe(frameworkHome);
  if (stat === undefined || !stat.isSymbolicLink()) {
    return false;
  }

  try {
    return readlinkSync(frameworkHome) === runtimeRoot;
  } catch {
    return false;
  }
}

/** lstat that returns undefined instead of throwing when the path is absent. */
function lstatSafe(path: string): Stats | undefined {
  try {
    return lstatSync(path);
  } catch {
    return undefined;
  }
}
